import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Group,
  Avatar,
  Title,
  Text,
  Badge,
  ThemeIcon,
  SimpleGrid,
  Box,
  Center,
  Button,
  Card,
  Divider,
  RingProgress,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconSchool,
  IconStethoscope,
  IconCalendar,
  IconMapPin,
  IconMail,
  IconPhone,
  IconLock,
  IconEdit,
  IconUserOff,
  IconUserPlus,
  IconRosetteDiscountCheckFilled,
} from '@tabler/icons-react';
import { IconUsers } from '@tabler/icons-react';
import PageContainer from '../components/ui/PageContainer';
import PageSkeleton from '../components/ui/PageSkeleton';
import { profileService } from '../services/profileService';
import { authService } from '../services/authService';
import { examenService } from '../services/examenService';
import { AmigosService } from '../services/amigosService';
import { ExamenEnProgreso } from '../types/examen.types';
import { AmistadDTO } from '../types/amigos';
import { colorPorNombre, mix, pastel, ink } from '../utils/especialidad';
import { banderaPais, nombrePais } from '../utils/paises';
import { perfilQuery } from '../queries/appQueries';

interface InfoItemProps {
  icon: React.ComponentType<{ size?: number; color?: string }>;
  label: string;
  value?: string | number | null;
}

// Fila de información sobria: icono tenue + etiqueta + valor, sin chips de colores.
const InfoItem: React.FC<InfoItemProps> = ({ icon: Icon, label, value }) => {
  const { colorScheme } = useMantineColorScheme();
  if (value === undefined || value === null || value === '') return null;
  const muted = colorScheme === 'dark' ? '#94a3b8' : '#8c8478';

  return (
    <Group gap="sm" align="center" wrap="nowrap" style={{ justifyContent: 'flex-start' }}>
      <Icon size={18} color={muted} />
      <Box style={{ minWidth: 0 }}>
        <Text size="xs" ta="left" style={{ color: muted, fontFamily: 'Inter, sans-serif' }}>
          {label}
        </Text>
        <Text
          size="sm"
          fw={600}
          ta="left"
          style={{
            color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
            fontFamily: 'Inter, sans-serif',
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Text>
      </Box>
    </Group>
  );
};

const PerfilPublicoPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  // El perfil se lee vía React Query para aprovechar el prefetch al iniciar sesión
  // (cache hit instantáneo en el perfil propio). Historial y amigos siguen aparte.
  const perfilQ = useQuery(perfilQuery(userId ? Number(userId) : undefined));
  const profile = perfilQ.data ?? null;
  const loading = perfilQ.isLoading;
  const error = perfilQ.isError || (!userId && !perfilQ.isLoading);

  const [historial, setHistorial] = useState<ExamenEnProgreso[]>([]);
  const [amigos, setAmigos] = useState<AmistadDTO[]>([]);

  const currentUser = authService.getCurrentUserFromStorage();
  const isOwnProfile = !!currentUser && String(currentUser.id) === String(userId);

  // Estilo de card plano, unificado con Configuración y Dashboard
  const cardStyle: React.CSSProperties = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26';
  const mutedColor = colorScheme === 'dark' ? '#94a3b8' : '#8c8478'; // gris cálido (no azul)
  const dividerColor = colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(45,42,38,0.1)';

  // Historial reciente de exámenes para la card lateral. Solo si las stats son
  // visibles (el backend ya filtra `stats` por privacidad) o es el propio perfil.
  useEffect(() => {
    if (!userId || !profile) return;
    const verActividad = isOwnProfile || (profile.perfilPublico !== false && !!profile.stats);
    if (!verActividad) {
      setHistorial([]);
      return;
    }
    let cancelled = false;
    examenService
      .obtenerHistorialExamenes(Number(userId))
      .then((h) => {
        if (!cancelled) setHistorial(Array.isArray(h) ? h : []);
      })
      .catch(() => {
        if (!cancelled) setHistorial([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, profile, isOwnProfile]);

  // Lista de amigos: solo tiene sentido en el perfil propio (el endpoint devuelve
  // los amigos del usuario autenticado). Sirve como lista de amigos agregados.
  useEffect(() => {
    if (!isOwnProfile) {
      setAmigos([]);
      return;
    }
    let cancelled = false;
    AmigosService.obtenerAmigos()
      .then((a) => {
        if (!cancelled) setAmigos(Array.isArray(a) ? a : []);
      })
      .catch(() => {
        if (!cancelled) setAmigos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOwnProfile]);

  if (loading) {
    return <PageSkeleton variant="list" count={3} />;
  }

  if (error || !profile) {
    return (
      <PageContainer>
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={18} />}
          onClick={() => navigate(-1)}
          mb="lg"
        >
          Volver
        </Button>
        <Card withBorder radius="lg" p="xl" style={cardStyle}>
          <Center>
            <Stack align="center" gap="sm" py="xl">
              <ThemeIcon size={64} radius="xl" style={{ backgroundColor: '#94a3b8', border: 'none' }}>
                <IconUserOff size={32} />
              </ThemeIcon>
              <Title order={3} style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                Perfil no disponible
              </Title>
              <Text c="dimmed" ta="center">
                No pudimos encontrar este usuario o el perfil ya no existe.
              </Text>
            </Stack>
          </Center>
        </Card>
      </PageContainer>
    );
  }

  const fullName = profileService.getFullName(profile);
  const initials = profileService.getInitials(profile);
  const avatarUrl = profile.avatar ? profileService.getAvatarUrl(profile.avatar) : null;
  const esPrivado = profile.perfilPublico === false && !isOwnProfile;

  const stats = !esPrivado ? profile.stats : undefined;
  const formatHoras = (seg?: number) => {
    if (!seg) return '0h';
    const h = Math.floor(seg / 3600);
    const m = Math.floor((seg % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Color base de la especialidad, pero suavizado: pastel para el banner y tinta
  // (oscuro) para el avatar/anillo. Nada de saturado.
  const baseColor = profile.especialidadInteres ? colorPorNombre(profile.especialidadInteres) : '#9a8f7d';
  const bannerColor = pastel(baseColor);
  const inkColor = ink(baseColor);

  // Actividad reciente (para la card lateral): % por examen, más recientes primero.
  const primerNombre = (profile.nombre || fullName).split(' ')[0];
  const actividad = [...historial]
    .sort(
      (a, b) =>
        new Date(b.finalizadoEn || b.iniciadoEn).getTime() -
        new Date(a.finalizadoEn || a.iniciadoEn).getTime()
    )
    .slice(0, 6)
    .map((h) => ({
      ...h,
      pct: h.totalPreguntas
        ? Math.round(((h.correctas ?? 0) / h.totalPreguntas) * 100)
        : typeof h.puntajeTotal === 'number'
        ? Math.round(h.puntajeTotal)
        : 0,
    }));

  return (
    <PageContainer>
      <style>{`
        @media (max-width: 900px) {
          .perfil-layout { flex-direction: column !important; }
          .perfil-main { max-width: 100% !important; }
          .perfil-aside { width: 100% !important; position: static !important; }
        }
        @media (max-width: 768px) {
          .perfil-hero-head { flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>

      <Box className="perfil-layout" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      <Box className="perfil-main" style={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
      {/* ===== Hero estilo Duolingo: banner + avatar superpuesto + identidad ===== */}
      <Card withBorder radius="lg" p={0} style={{ ...cardStyle, overflow: 'hidden', marginBottom: '0.75rem' }}>
        {/* Banner de color plano (color de la especialidad de interés) */}
        <Box style={{ height: 76, backgroundColor: bannerColor }} />

        <Box style={{ padding: '0 1.1rem 1.1rem' }}>
          {/* Avatar superpuesto al banner */}
          <Avatar
            size={88}
            src={avatarUrl}
            radius="50%"
            style={{
              marginTop: -44,
              border: `3px solid ${colorScheme === 'dark' ? '#1e293b' : '#f7f3ee'}`,
              backgroundColor: inkColor,
            }}
          >
            <Text fw={700} style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '1.6rem', color: 'white' }}>
              {initials}
            </Text>
          </Avatar>

          <Group className="perfil-hero-head" justify="space-between" align="flex-start" wrap="nowrap" mt={6}>
            <Box style={{ minWidth: 0 }}>
              <Group gap={6} align="center" wrap="nowrap">
                <Title
                  order={3}
                  ta="left"
                  style={{
                    fontFamily: 'Space Grotesk, Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    letterSpacing: '-0.02em',
                    color: textColor,
                    margin: 0,
                  }}
                >
                  {fullName}
                </Title>
                {profile.emailVerificado && (
                  <IconRosetteDiscountCheckFilled size={20} color="#1d9bf0" style={{ flexShrink: 0 }} aria-label="Cuenta verificada" />
                )}
              </Group>

              <Text size="xs" ta="left" style={{ fontFamily: 'Inter, sans-serif', color: mutedColor }}>
                @{profile.username}
              </Text>

              {/* Línea meta: se unió + contadores sociales, todo junto y compacto */}
              <Group gap="md" mt={6} align="center" wrap="wrap">
                {profile.fechaRegistro && (
                  <Group gap={5} align="center">
                    <IconCalendar size={14} color={mutedColor} />
                    <Text size="xs" style={{ fontFamily: 'Inter, sans-serif', color: mutedColor }}>
                      Se unió en{' '}
                      {new Date(profile.fechaRegistro).toLocaleDateString('es-MX', { year: 'numeric', month: 'long' })}
                    </Text>
                  </Group>
                )}
                {typeof profile.amigosCount === 'number' && (
                  <Group gap={5} align="center">
                    <IconUsers size={14} color={mutedColor} />
                    <Text size="xs" style={{ fontFamily: 'Inter, sans-serif', color: textColor }}>
                      <b>{profile.amigosCount}</b>{' '}
                      <span style={{ color: mutedColor }}>{profile.amigosCount === 1 ? 'amigo' : 'amigos'}</span>
                    </Text>
                  </Group>
                )}
                {!isOwnProfile && typeof profile.amigosEnComun === 'number' && profile.amigosEnComun > 0 && (
                  <Text size="xs" style={{ fontFamily: 'Inter, sans-serif', color: textColor }}>
                    <b>{profile.amigosEnComun}</b>{' '}
                    <span style={{ color: mutedColor }}>en común</span>
                  </Text>
                )}
              </Group>
            </Box>

            {isOwnProfile && (
              <Button
                size="xs"
                variant="default"
                radius="md"
                leftSection={<IconEdit size={14} />}
                onClick={() => navigate('/settings')}
                style={{ flexShrink: 0, color: textColor }}
              >
                Editar perfil
              </Button>
            )}
          </Group>
        </Box>
      </Card>

      {/* ===== Detalles (sueltos, sin tarjetas, paleta sobria) ===== */}
      {esPrivado ? (
        <Stack align="center" gap="xs" py={48}>
          <IconLock size={30} color={mutedColor} />
          <Title order={4} style={{ fontFamily: 'Space Grotesk, Inter, sans-serif', color: textColor }}>
            Este perfil es privado
          </Title>
          <Text ta="center" maw={420} style={{ color: mutedColor }}>
            {fullName.split(' ')[0]} decidió mantener su información oculta. Solo puedes ver su nombre y foto.
          </Text>
        </Stack>
      ) : (
        <Box px={4}>
          {/* Estadísticas: números en línea + anillo, sin cajas de colores */}
          {stats && (
            <Box mb="lg">
              <Group justify="space-between" align="center" wrap="nowrap">
                <Group gap="xl" align="center" wrap="wrap">
                  {[
                    { valor: String(stats.simulacrosCompletados ?? 0), label: 'Simulacros' },
                    { valor: formatHoras(stats.tiempoEstudioTotal), label: 'Tiempo de estudio' },
                  ].map((s, i) => (
                    <React.Fragment key={s.label}>
                      {i > 0 && <Box style={{ width: 1, height: 30, backgroundColor: dividerColor }} />}
                      <Box>
                        <Text fw={700} style={{ fontSize: '1.5rem', color: textColor, fontFamily: 'Space Grotesk, sans-serif', lineHeight: 1.1 }}>
                          {s.valor}
                        </Text>
                        <Text size="xs" mt={2} style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                          {s.label}
                        </Text>
                      </Box>
                    </React.Fragment>
                  ))}
                </Group>
                {typeof stats.nivelProgreso === 'number' && (
                  <Box style={{ textAlign: 'center', flexShrink: 0 }}>
                    <RingProgress
                      size={68}
                      thickness={7}
                      roundCaps
                      sections={[{ value: Math.min(100, Math.max(0, stats.nivelProgreso)), color: inkColor }]}
                      label={
                        <Text ta="center" fw={700} style={{ fontSize: '0.8rem', color: textColor, fontFamily: 'Space Grotesk, sans-serif' }}>
                          {Math.round(stats.nivelProgreso)}%
                        </Text>
                      }
                    />
                    <Text size="xs" mt={2} style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                      Promedio
                    </Text>
                  </Box>
                )}
              </Group>
            </Box>
          )}

          {/* Sobre mí: texto suelto */}
          {profile.bio && (
            <>
              <Divider color={dividerColor} />
              <Box my="lg">
                <Text size="xs" fw={700} ta="left" mb={6} style={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                  Sobre mí
                </Text>
                <Text ta="left" style={{ color: textColor, fontFamily: 'Inter, sans-serif', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {profile.bio}
                </Text>
              </Box>
            </>
          )}

          {/* Información: lista en dos columnas, iconos tenues */}
          <Divider color={dividerColor} />
          <Box mt="lg">
            <Text size="xs" fw={700} ta="left" mb="md" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
              Información
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="md">
              <InfoItem icon={IconStethoscope} label="Especialidad de interés" value={profile.especialidadInteres} />
              <InfoItem icon={IconSchool} label="Universidad" value={profile.universidad} />
              <InfoItem icon={IconCalendar} label="Año de graduación" value={profile.anioGraduacion} />
              <InfoItem
                icon={IconMapPin}
                label="País"
                value={profile.pais ? `${banderaPais(profile.pais)} ${nombrePais(profile.pais)}`.trim() : undefined}
              />
              <InfoItem icon={IconMail} label="Correo" value={profile.email} />
              <InfoItem icon={IconPhone} label="Teléfono" value={profile.telefono} />
            </SimpleGrid>

            {!profile.especialidadInteres &&
              !profile.universidad &&
              !profile.anioGraduacion &&
              !profile.pais &&
              !profile.email &&
              !profile.telefono && (
                <Text ta="center" py="md" style={{ color: mutedColor }}>
                  Este usuario aún no ha compartido información pública.
                </Text>
              )}
          </Box>
        </Box>
      )}
      </Box>

      {/* ===== Columna derecha: actividad + amigos ===== */}
      {!esPrivado && (
        <Box className="perfil-aside" style={{ width: 300, flexShrink: 0, position: 'sticky', top: 16 }}>
        <Stack gap="sm">
          {/* Mis amigos — solo en el perfil propio; va arriba del historial para acceso rápido */}
          {isOwnProfile && (
            <Card withBorder radius="lg" p="lg" style={cardStyle}>
              <Text size="xs" fw={700} mb="md" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                Mis amigos{amigos.length > 0 ? ` (${amigos.length})` : ''}
              </Text>

              {amigos.length === 0 ? (
                <Text size="sm" py="sm" style={{ color: mutedColor }}>
                  Aún no tienes amigos agregados.
                </Text>
              ) : (
                <Stack gap="sm">
                  {amigos.slice(0, 5).map((a) => {
                    const nombre =
                      a.amigo.nombre && a.amigo.apellidos
                        ? `${a.amigo.nombre} ${a.amigo.apellidos}`
                        : a.amigo.username;
                    return (
                      <Group
                        key={a.id}
                        gap="sm"
                        wrap="nowrap"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/perfil/${a.amigo.id}`)}
                      >
                        <Avatar src={profileService.getAvatarUrl(a.amigo.avatarUrl || null) || undefined} radius="xl" size="sm" style={{ backgroundColor: inkColor, color: 'white' }}>
                          {(a.amigo.nombre?.[0] || a.amigo.username?.[0] || '?').toUpperCase()}
                        </Avatar>
                        <Box style={{ minWidth: 0 }}>
                          <Text size="sm" fw={600} truncate style={{ color: textColor, fontFamily: 'Inter, sans-serif' }}>
                            {nombre}
                          </Text>
                          <Text size="xs" ta="left" truncate style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                            @{a.amigo.username}
                          </Text>
                        </Box>
                      </Group>
                    );
                  })}
                </Stack>
              )}

              <Button
                fullWidth
                mt="md"
                size="xs"
                variant="light"
                color="gray"
                leftSection={<IconUserPlus size={14} />}
                onClick={() => navigate('/estudiante/solicitudes')}
                style={{ color: textColor }}
              >
                Amigos y Solicitudes
              </Button>
            </Card>
          )}

          <Card withBorder radius="lg" p="lg" style={cardStyle}>
            <Text size="xs" fw={700} mb={2} style={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
              Exámenes recientes
            </Text>
            <Text size="xs" mb="md" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
              {isOwnProfile ? 'Tus últimos resultados' : `Cómo le ha ido a ${primerNombre}`}
            </Text>

            {actividad.length === 0 ? (
              <Text size="sm" ta="left" py="sm" style={{ color: mutedColor }}>
                Sin exámenes recientes.
              </Text>
            ) : (
              <Stack gap="sm">
                {actividad.map((h, i) => {
                  const aprob = h.pct >= 60;
                  const chip = aprob ? '#7c9a6d' : '#b08968'; // verde salvia / terracota suaves
                  return (
                    <React.Fragment key={h.intentoId ?? i}>
                      {i > 0 && <Divider color={dividerColor} />}
                      <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
                        <Box style={{ minWidth: 0 }}>
                          <Text size="sm" fw={600} ta="left" truncate style={{ color: textColor, fontFamily: 'Inter, sans-serif' }}>
                            {h.nombreExamen || h.tipoExamen || 'Examen'}
                          </Text>
                          {h.finalizadoEn && (
                            <Text size="xs" ta="left" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                              {new Date(h.finalizadoEn).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                            </Text>
                          )}
                        </Box>
                        <Badge
                          radius="sm"
                          style={{
                            flexShrink: 0,
                            color: ink(chip),
                            backgroundColor: pastel(chip),
                            border: `1px solid ${mix(chip, '#f4efe7', 0.55)}`,
                            fontWeight: 700,
                          }}
                        >
                          {h.pct}%
                        </Badge>
                      </Group>
                    </React.Fragment>
                  );
                })}
              </Stack>
            )}
          </Card>
        </Stack>
        </Box>
      )}
      </Box>
    </PageContainer>
  );
};

export default PerfilPublicoPage;
